/**
 * Shared redirect helper utilities
 * Used by multiple redirect plugins to avoid code duplication
 *
 * Note: Functions that use Nuxt composables internally must be called
 * from this composable to ensure proper context.
 */
import type { H3Event } from 'h3'
import type { DatadogContext } from '~~/layers/base/types/datadog'

interface MagentoProductResponse {
    name: string
    sku: string
    custom_attributes?: Array<{
        attribute_code: string
        value: string
    }>
}

/**
 * Pure utility function - doesn't use composables
 * Extract SKU from route path
 */
function extractSkuFromPath(path: string): string | null {
    // Common patterns: /designer/start/SKU, /designer/personalize/SKU, /start/SKU, etc.
    const patterns = [
        /\/designer\/(?:start|personalize|printing-process|resume)\/([^/]+)/,
        /\/start\/([^/]+)/,
        /\/designer\/[^/]+\/([^/]+)/,
    ]

    for (const pattern of patterns) {
        const match = path.match(pattern)
        if (match && match[1]) {
            // Basic validation: SKUs are typically alphanumeric with dashes/underscores
            const potentialSku = match[1]
            if (potentialSku && potentialSku.length > 0 && potentialSku.length < 100) {
                return potentialSku
            }
        }
    }

    return null
}

/**
 * Pure utility function - doesn't use composables
 * Check if an error status code should trigger a redirect (404 or 400)
 */
function shouldRedirectOnError(statusCode?: number): boolean {
    return statusCode === 404 || statusCode === 400
}

/**
 * Pure utility function - doesn't use composables
 * Extract status code from error object
 */
function getErrorStatusCode(error: unknown): number | undefined {
    if (error && typeof error === 'object') {
        const errorObj = error as { statusCode?: number; status?: number }
        return errorObj.statusCode || errorObj.status
    }
    return undefined
}

/**
 * Main composable - returns all functions that use Nuxt composables
 */
export function useRedirectHelpers() {
    /**
     * Get runtime config values for redirects
     * @param event - Optional H3Event for server-side usage (required in server middleware)
     */
    const getRedirectConfig = (event?: H3Event) => {
        const config = (event ? useRuntimeConfig(event) : useRuntimeConfig()) as {
            public?: {
                storefrontUrl?: string
                magentoApiUrl?: string
            }
        }

        if (!config.public?.storefrontUrl) {
            logErrorToDatadog(
                { message: 'Storefront URL not configured' },
                { source: 'redirect-helpers' }
            )
        }

        return {
            storefrontUrl: config.public?.storefrontUrl || 'https://crane.com',
            magentoApiUrl: config.public?.magentoApiUrl,
        }
    }

    /**
     * Get product URL from Magento API using SKU
     */
    const getProductUrlFromMagento = async (
        sku: string,
        magentoApiUrl: string,
        storefrontUrl: string
    ): Promise<string | null> => {
        try {
            const response = (await $fetch(
                `${magentoApiUrl}/rest/V1/products/${encodeURIComponent(sku)}`
            )) as MagentoProductResponse

            // Extract url_key from custom_attributes
            if (response?.custom_attributes) {
                const urlKeyAttr = response.custom_attributes.find(
                    (attr) => attr.attribute_code === 'url_key'
                )
                if (urlKeyAttr?.value && urlKeyAttr.value.trim()) {
                    return `${storefrontUrl}/${urlKeyAttr.value.trim()}`
                }
            }
        } catch (error) {
            // Log error but don't throw - we'll fallback
            logErrorToDatadog(error, {
                source: 'redirect-helpers',
                sku,
                path: window.location.pathname,
            })
        }

        return null
    }

    /**
     * Get redirect URL for a given SKU
     * Tries Magento API first, falls back to storefront homepage, then crane.com
     */
    const getRedirectUrlForSku = async (sku: string | null): Promise<string> => {
        const { storefrontUrl, magentoApiUrl } = getRedirectConfig()

        if (!sku) {
            // No SKU, redirect to storefront homepage
            return storefrontUrl
        }

        // Try to get product URL from Magento
        if (magentoApiUrl) {
            const productUrl = await getProductUrlFromMagento(sku, magentoApiUrl, storefrontUrl)
            if (productUrl) {
                return productUrl
            }
        }

        // Fallback to storefront homepage
        return storefrontUrl
    }

    /**
     * Get redirect URL for invalid routes
     * Extracts SKU from path, tries to find product page, falls back to crane.com
     */
    const getRedirectUrlForInvalidRoute = async (
        path: string,
        routeParams?: Record<string, string | string[]>
    ): Promise<string> => {
        const { storefrontUrl } = getRedirectConfig()

        const sku = extractSkuFromPath(path) || routeParams?.sku?.toString()

        if (sku) {
            // Try to get product URL or storefront URL
            const url = await getRedirectUrlForSku(sku)
            if (url) {
                return url
            }
        }

        // Final fallback to storefront homepage
        return storefrontUrl
    }

    /**
     * Handle error redirect for 404/400 errors
     * Returns true if redirect was handled, false otherwise
     */
    const handleErrorRedirect = async (
        error: unknown,
        route: ReturnType<typeof useRoute>
    ): Promise<boolean> => {
        const statusCode = getErrorStatusCode(error)

        const failingPath =
            (typeof error === 'object' && (error as { url?: string }).url) || route?.path || ''

        // Only allow redirects for designer flows
        const DESIGNER_PATH_REGEX = /^\/designer\/(start|personalize)\//

        if (!DESIGNER_PATH_REGEX.test(failingPath)) {
            return false // skip redirect; let the error surface normally
        }

        if (shouldRedirectOnError(statusCode)) {
            const redirectUrl = await getRedirectUrlForInvalidRoute(route.path, route.params)
            await navigateTo(redirectUrl, { external: true })
            return true
        }

        return false
    }

    /**
     * Log error to Datadog (non-blocking)
     */
    const logErrorToDatadog = (error: unknown, context: DatadogContext = {}): void => {
        Promise.resolve()
            .then(() => {
                const datadog = useDatadog()
                const errorObj = error instanceof Error ? error : new Error(String(error))

                if (datadog) {
                    datadog.logError(errorObj, context)
                }
            })
            .catch(() => {
                // Silently fail - if Datadog logging fails, don't break anything
            })
    }

    return {
        getRedirectConfig,
        getProductUrlFromMagento,
        getRedirectUrlForSku,
        getRedirectUrlForInvalidRoute,
        handleErrorRedirect,
        logErrorToDatadog,
    }
}
